/**
 * Organization Structure Page
 * Board of Directors, Management Committee, and Organizational Chart
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  Home,
  Users,
  Landmark,
  UserCircle,
  UserCheck,
  Building2,
} from 'lucide-react';
import axios from 'axios';
import { isSafeImageUrl } from '../../../utils/isSafeUrl';

const CMS_API_URL = process.env.REACT_APP_API_BASE_URL_CMS || '';
const AUTH_USERNAME = process.env.REACT_APP_USERNAME || '';
const AUTH_PASSWORD = process.env.REACT_APP_PASSWORD || '';

/* ─── Hero ─── */
const OrgStructureHero = () => {
  const [heroData, setHeroData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHero = async () => {
      try {
        const response = await axios.post(
          `${CMS_API_URL}sections/get-by-uid`,
          { UID: 'ORG-STRUCTURE-HERO' },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
            auth: { username: AUTH_USERNAME, password: AUTH_PASSWORD },
          }
        );

        if (response.data?.code === 1 && Array.isArray(response.data?.data) && response.data.data.length > 0) {
          const data = response.data.data[0];
          if (data.IsDeleted === false && data.IsPublished === true) {
            setHeroData(data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch org structure hero:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHero();
  }, []);

  const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim();
  };

  if (!loading && !heroData) {
    return null;
  }

  if (loading) {
    return (
      <section className="bg-[#2f3951] py-5 md:py-5">
        <div className="container mx-auto px-6 md:px-8 lg:px-12">
          <div className="h-4 w-48 bg-gray-600 rounded mb-3 animate-pulse" />
          <div className="h-8 w-64 bg-gray-600 rounded mb-2 animate-pulse" />
          <div className="w-24 h-1 bg-gray-600 mb-3 animate-pulse" />
          <div className="h-4 w-full max-w-2xl bg-gray-600 rounded animate-pulse" />
        </div>
      </section>
    );
  }

  return (
    <section className="bg-[#2f3951] py-5 md:py-5">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        <nav className="flex items-center text-white/80 text-sm mb-3">
          <Link to="/" className="flex items-center hover:text-[#ca9a37] transition-colors">
            <Home className="w-4 h-4 mr-1" />
            Home
          </Link>
          <ChevronRight className="w-4 h-4 mx-2" />
          <Link to="/about" className="hover:text-[#ca9a37] transition-colors">
            About Us
          </Link>
          <ChevronRight className="w-4 h-4 mx-2" />
          <span className="text-[#ca9a37]">{heroData?.SubTitle || 'Organization Structure'}</span>
        </nav>

        {heroData?.Title && (
          <h1 className="text-2xl md:text-4xl font-bold text-white mb-2">
            {heroData.Title}
          </h1>
        )}
        <div className="w-24 h-1 bg-[#ca9a37] mb-3"></div>
        {heroData?.Description && (
          <p className="text-base text-gray-200 max-w-2xl line-clamp-3">
            {stripHtml(heroData.Description)}
          </p>
        )}
      </div>
    </section>
  );
};

/* ─── Person Card ─── */
const PersonCard = ({ name, role, subtitle, image }) => (
  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center hover:-translate-y-1 transition-transform h-full flex flex-col">
    <div className="w-32 h-32 mx-auto rounded-xl bg-[#f8f1e5] flex items-center justify-center mb-2 overflow-hidden shadow-sm border border-gray-100">
      {image && isSafeImageUrl(image) ? (
        <img src={image} alt={name} className="w-full h-full object-cover" />
      ) : (
        <UserCircle className="w-10 h-10 text-[#ca9a37]/50" />
      )}
    </div>
    <h4 className="font-bold text-[#2f3951] mb-1">{name}</h4>
    <p className="text-[#ca9a37] text-sm font-medium">{role}</p>
    {subtitle && <p className="text-gray-400 text-xs mt-1">{subtitle}</p>}
  </div>
);

/* ─── Section Header ─── */
const SectionHeader = ({ icon: Icon, title, description }) => (
  <div className="mb-8">
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-5 h-5 text-[#ca9a37]" />
      <h2 className="text-2xl md:text-3xl font-bold text-[#2f3951]">{title}</h2>
    </div>
    <div className="w-16 h-1 bg-[#ca9a37] mb-3"></div>
    {description && <p className="text-gray-600 text-sm">{description}</p>}
  </div>
);

const resolveSectionIcon = (linkValue) => {
  const map = {
    Users: Users,
    User: UserCheck,
    Landmark: Landmark,
    UserCheck: UserCheck,
  };
  return map[linkValue] || Building2;
};

/* ─── Board of Directors ─── */
const BoardOfDirectors = () => {
  const [section, setSection] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sectionRes, membersRes] = await Promise.all([
          axios.post(
            `${CMS_API_URL}sections/get-by-uid`,
            { UID: 'BOARD-MEMBERS' },
            {
              headers: { 'Content-Type': 'application/json' },
              timeout: 30000,
              auth: { username: AUTH_USERNAME, password: AUTH_PASSWORD },
            }
          ),
          axios.post(
            `${CMS_API_URL}media/by-filter`,
            { CategoryId: 59, Type: 'BOARD-MEMBERS' },
            {
              headers: { 'Content-Type': 'application/json' },
              timeout: 30000,
              auth: { username: AUTH_USERNAME, password: AUTH_PASSWORD },
            }
          ),
        ]);

        if (sectionRes.data?.code === 1 && Array.isArray(sectionRes.data?.data) && sectionRes.data.data.length > 0) {
          const sec = sectionRes.data.data[0];
          if (sec.IsDeleted === false && sec.IsPublished === true) {
            setSection(sec);
          }
        }

        if (membersRes.data?.code === 1 && Array.isArray(membersRes.data?.data)) {
          const items = membersRes.data.data
            .filter((m) => m.IsDeleted === false && m.IsPublished === true)
            .map((m) => ({
              id: String(m.MediaId ?? m.Id ?? Math.random()),
              name: m.Title ?? '',
              role: m.SubTitle ?? '',
              image: m.Link ?? null,
            }));
          setMembers(items);
        }
      } catch (error) {
        console.error('Failed to fetch board members:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (!loading && !section && members.length === 0) {
    return null;
  }

  const sectionIcon = resolveSectionIcon(section?.Link);

  return (
    <section className="py-14 bg-white">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        {loading ? (
          <div className="mb-8 animate-pulse">
            <div className="h-8 w-64 bg-gray-200 rounded mb-2" />
            <div className="w-16 h-1 bg-gray-200 mb-3" />
            <div className="h-4 w-full max-w-lg bg-gray-200 rounded" />
          </div>
        ) : section ? (
          <SectionHeader
            icon={sectionIcon}
            title={section.Title}
            description={section.SubTitle || section.Description}
          />
        ) : null}
        {loading ? (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center">
                <div className="w-28 h-28 mx-auto rounded-xl bg-gray-200 mb-4 animate-pulse" />
                <div className="h-4 w-24 bg-gray-200 rounded mx-auto mb-2 animate-pulse" />
                <div className="h-3 w-32 bg-gray-200 rounded mx-auto animate-pulse" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
            {members.map((m) => (
              <PersonCard key={m.id} name={m.name} role={m.role} image={m.image} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

/* ─── Management Committee ─── */
const ManagementCommittee = () => {
  const [section, setSection] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sectionRes, membersRes] = await Promise.all([
          axios.post(
            `${CMS_API_URL}sections/get-by-uid`,
            { UID: 'MANAGEMENT-COMITTEE' },
            {
              headers: { 'Content-Type': 'application/json' },
              timeout: 30000,
              auth: { username: AUTH_USERNAME, password: AUTH_PASSWORD },
            }
          ),
          axios.post(
            `${CMS_API_URL}media/by-filter`,
            { CategoryId: 59, Type: 'MANAGEMENT-COMITTEE' },
            {
              headers: { 'Content-Type': 'application/json' },
              timeout: 30000,
              auth: { username: AUTH_USERNAME, password: AUTH_PASSWORD },
            }
          ),
        ]);

        if (sectionRes.data?.code === 1 && Array.isArray(sectionRes.data?.data) && sectionRes.data.data.length > 0) {
          const sec = sectionRes.data.data[0];
          if (sec.IsDeleted === false && sec.IsPublished === true) {
            setSection(sec);
          }
        }

        if (membersRes.data?.code === 1 && Array.isArray(membersRes.data?.data)) {
          const items = membersRes.data.data
            .filter((m) => m.IsDeleted === false && m.IsPublished === true)
            .map((m) => ({
              id: String(m.MediaId ?? m.Id ?? Math.random()),
              name: m.Title ?? '',
              role: m.SubTitle ?? '',
              image: m.Link ?? null,
            }));
          setMembers(items);
        }
      } catch (error) {
        console.error('Failed to fetch management committee:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (!loading && !section && members.length === 0) {
    return null;
  }

  const sectionIcon = resolveSectionIcon(section?.Link);

  return (
    <section className="py-14 bg-[#fafafa]">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        {loading ? (
          <div className="mb-8 animate-pulse">
            <div className="h-8 w-64 bg-gray-200 rounded mb-2" />
            <div className="w-16 h-1 bg-gray-200 mb-3" />
            <div className="h-4 w-full max-w-lg bg-gray-200 rounded" />
          </div>
        ) : section ? (
          <SectionHeader
            icon={sectionIcon}
            title={section.Title}
            description={section.SubTitle || section.Description}
          />
        ) : null}
        {loading ? (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center">
                <div className="w-28 h-28 mx-auto rounded-xl bg-gray-200 mb-4 animate-pulse" />
                <div className="h-4 w-24 bg-gray-200 rounded mx-auto mb-2 animate-pulse" />
                <div className="h-3 w-32 bg-gray-200 rounded mx-auto animate-pulse" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
            {members.map((m) => (
              <PersonCard key={m.id} name={m.name} role={m.role} image={m.image} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

/* ─── Actors ─── */
const Actors = () => {
  const [section, setSection] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sectionRes, membersRes] = await Promise.all([
          axios.post(
            `${CMS_API_URL}sections/get-by-uid`,
            { UID: 'ACTOR' },
            {
              headers: { 'Content-Type': 'application/json' },
              timeout: 30000,
              auth: { username: AUTH_USERNAME, password: AUTH_PASSWORD },
            }
          ),
          axios.post(
            `${CMS_API_URL}media/by-filter`,
            { CategoryId: 59, Type: 'ACTOR' },
            {
              headers: { 'Content-Type': 'application/json' },
              timeout: 30000,
              auth: { username: AUTH_USERNAME, password: AUTH_PASSWORD },
            }
          ),
        ]);

        if (sectionRes.data?.code === 1 && Array.isArray(sectionRes.data?.data) && sectionRes.data.data.length > 0) {
          const sec = sectionRes.data.data[0];
          if (sec.IsDeleted === false && sec.IsPublished === true) {
            setSection(sec);
          }
        }

        if (membersRes.data?.code === 1 && Array.isArray(membersRes.data?.data)) {
          const items = membersRes.data.data
            .filter((m) => m.IsDeleted === false && m.IsPublished === true)
            .map((m) => ({
              id: String(m.MediaId ?? m.Id ?? Math.random()),
              name: m.Title ?? '',
              role: m.SubTitle ?? '',
              image: m.Link ?? null,
            }));
          setMembers(items);
        }
      } catch (error) {
        console.error('Failed to fetch actors:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (!loading && !section && members.length === 0) {
    return null;
  }

  const sectionIcon = resolveSectionIcon(section?.Link);

  return (
    <section className="py-14 bg-white">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        {loading ? (
          <div className="mb-8 animate-pulse">
            <div className="h-8 w-64 bg-gray-200 rounded mb-2" />
            <div className="w-16 h-1 bg-gray-200 mb-3" />
            <div className="h-4 w-full max-w-lg bg-gray-200 rounded" />
          </div>
        ) : section ? (
          <SectionHeader
            icon={sectionIcon}
            title={section.Title}
            description={section.SubTitle || section.Description}
          />
        ) : null}
        {loading ? (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center">
                <div className="w-28 h-28 mx-auto rounded-xl bg-gray-200 mb-4 animate-pulse" />
                <div className="h-4 w-24 bg-gray-200 rounded mx-auto mb-2 animate-pulse" />
                <div className="h-3 w-32 bg-gray-200 rounded mx-auto animate-pulse" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
            {members.map((m) => (
              <PersonCard key={m.id} name={m.name} role={m.role} image={m.image} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

/* ─── Organizational Chart ─── */
const OrganizationalChart = () => {
  const [section, setSection] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSection = async () => {
      try {
        const response = await axios.post(
          `${CMS_API_URL}sections/get-by-uid`,
          { UID: 'ORGANISATION-STRUCTURE' },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
            auth: { username: AUTH_USERNAME, password: AUTH_PASSWORD },
          }
        );

        if (response.data?.code === 1 && Array.isArray(response.data?.data) && response.data.data.length > 0) {
          const sec = response.data.data[0];
          if (sec.IsDeleted === false && sec.IsPublished === true) {
            setSection(sec);
          }
        }
      } catch (error) {
        console.error('Failed to fetch org structure section:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSection();
  }, []);

  if (!loading && !section) {
    return null;
  }

  return (
    <section className="py-14 bg-white">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        {loading ? (
          <div className="text-center mb-10 animate-pulse">
            <div className="h-8 w-64 bg-gray-200 rounded mx-auto mb-2" />
            <div className="w-16 h-1 bg-gray-200 mx-auto" />
          </div>
        ) : section ? (
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-[#2f3951] mb-2">{section.Title}</h2>
            <div className="w-16 h-1 bg-[#ca9a37] mx-auto"></div>
            {section.SubTitle && <p className="text-gray-600 text-sm mt-3">{section.SubTitle}</p>}
          </div>
        ) : null}

        <div className="flex flex-col items-center max-w-2xl mx-auto">
          {/* Board of Directors */}
          <div className="bg-[#2f3951] text-white px-8 py-3 rounded-lg font-semibold text-sm shadow-sm">
            Board of Directors
          </div>

          {/* Vertical line */}
          <div className="w-0.5 h-8 bg-[#ca9a37]"></div>

          {/* General Manager */}
          <div className="bg-[#ca9a37] text-white px-8 py-3 rounded-lg font-semibold text-sm shadow-sm">
            General Manager
          </div>

          {/* Vertical line + horizontal connector */}
          <div className="w-0.5 h-6 bg-[#ca9a37]"></div>
          <div className="w-full flex justify-center">
            <div className="w-0.5 h-4 bg-[#ca9a37] absolute"></div>
            <div className="w-full h-0.5 bg-[#ca9a37] mt-4" style={{ maxWidth: '400px' }}></div>
          </div>

          {/* Department row */}
          <div className="grid grid-cols-3 gap-4 mt-6 w-full" style={{ maxWidth: '400px' }}>
            <div className="flex flex-col items-center">
              <div className="w-0.5 h-4 bg-[#ca9a37]"></div>
              <div className="bg-white border border-gray-200 text-[#2f3951] px-4 py-2 rounded-lg font-medium text-sm text-center w-full shadow-sm">
                Finance
              </div>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-0.5 h-4 bg-[#ca9a37]"></div>
              <div className="bg-white border border-gray-200 text-[#2f3951] px-4 py-2 rounded-lg font-medium text-sm text-center w-full shadow-sm">
                ICT
              </div>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-0.5 h-4 bg-[#ca9a37]"></div>
              <div className="bg-white border border-gray-200 text-[#2f3951] px-4 py-2 rounded-lg font-medium text-sm text-center w-full shadow-sm">
                Operations
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── Page ─── */
const OrganizationStructurePage = () => (
  <div>
    <OrgStructureHero />
    <BoardOfDirectors />
    <ManagementCommittee />
    <Actors />
    <OrganizationalChart />
  </div>
);

export default OrganizationStructurePage;
